// [PATCH pdfjs-predogled v1] 12.10.2026 — PDF predogled z pdf.js (react-pdf 7.7.3) namesto nativnega iframe.
// Stran se vedno prilega panelu (po širini ali cela stran) in se ob spremembi velikosti sama prilagodi —
// enako v Chrome, Edge, Firefox, Safari. PDF se prenese enkrat v celoti (kratkoživi žeton ne poteče med
// branjem po kosih). Slike se prikažejo kot slika; ob napaki pdf.js se pokaže stari iframe (rezervna pot).
//
// Odvisnosti: react-pdf 7.7.3 (s seboj pdfjs-dist 3.11.174); worker v public/pdf.worker.min.js.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import 'react-pdf/dist/Page/TextLayer.css';
import { ZoomIn, ZoomOut, MoveHorizontal, Maximize, RotateCw, Download } from 'lucide-react';
import { api } from '../lib/api';

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;

const API_BASE = process.env.REACT_APP_API_URL || '/api/v1';

const ODMIK = 12;        // rob okoli strani (px)
const DRSNIK = 12;       // prostor za navpični drsnik (px)
const KORAK_ZOOM = 0.15;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;

// ───────────────────────── prenos datoteke ─────────────────────────

type Vsebina = { tip: 'pdf' | 'slika' | 'drugo'; podatki: Uint8Array; blobUrl: string };

function usePrenos(url: string | null) {
  const [vsebina, setVsebina] = useState<Vsebina | null>(null);
  const [napaka, setNapaka] = useState<string | null>(null);

  useEffect(() => {
    if (!url) return undefined;
    let preklicano = false;
    let blobUrl = '';
    setVsebina(null);
    setNapaka(null);

    fetch(url)
      .then(async (r) => {
        if (!r.ok) throw new Error(String(r.status));
        const ct = (r.headers.get('content-type') || '').toLowerCase();
        const buf = await r.arrayBuffer();
        if (preklicano) return;
        const bajti = new Uint8Array(buf);
        // %PDF na začetku datoteke = PDF, ne glede na content-type
        const jePdf = ct.includes('pdf') || (bajti[0] === 0x25 && bajti[1] === 0x50 && bajti[2] === 0x44 && bajti[3] === 0x46);
        const tip: Vsebina['tip'] = jePdf ? 'pdf' : ct.startsWith('image/') ? 'slika' : 'drugo';
        blobUrl = URL.createObjectURL(new Blob([buf], { type: jePdf ? 'application/pdf' : ct || 'application/octet-stream' }));
        setVsebina({ tip, podatki: bajti, blobUrl });
      })
      .catch(() => { if (!preklicano) setNapaka('PDF ni na voljo'); });

    return () => {
      preklicano = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [url]);

  return { vsebina, napaka };
}

// ───────────────────────── prikaz ─────────────────────────

function Nalaganje() {
  return (
    <div className="flex items-center justify-center h-64 text-gray-400">
      <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mr-2"></div>
      Nalaganje...
    </div>
  );
}

function Gumb({ naslov, aktiven = false, onClick, children }: {
  naslov: string; aktiven?: boolean; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <button type="button" title={naslov} onClick={onClick}
      className={'h-7 w-7 flex items-center justify-center rounded hover:bg-gray-700 ' + (aktiven ? 'bg-gray-700 text-white' : '')}>
      {children}
    </button>
  );
}

/** Prikaže datoteko z danega URL-ja: PDF prek pdf.js, slika kot <img>, ostalo (ali napaka pdf.js) v iframe. */
export function PdfPlatno({ url, imeDatoteke = 'dokument.pdf' }: { url: string; imeDatoteke?: string }) {
  const { vsebina, napaka } = usePrenos(url);
  const [pdfNapaka, setPdfNapaka] = useState(false);

  const okvirRef = useRef<HTMLDivElement>(null);
  const [velikost, setVelikost] = useState({ sirina: 0, visina: 0 });
  const [steviloStrani, setSteviloStrani] = useState(0);
  const [razmerje, setRazmerje] = useState(1.414); // višina / širina prve strani
  const [nacin, setNacin] = useState<'sirina' | 'stran'>('sirina');
  const [zoom, setZoom] = useState(1);
  const [rotacija, setRotacija] = useState(0);

  // pdf.js podatke preda workerju (buffer se "izprazni"), zato mu vedno damo kopijo
  const datoteka = useMemo(() => (vsebina?.tip === 'pdf' ? { data: vsebina.podatki.slice() } : null), [vsebina]);

  // Spremljaj velikost okvirja (zunanji element ne drsi, da drsnik ne povzroča nihanja).
  useEffect(() => {
    const el = okvirRef.current;
    if (!el) return undefined;
    let casovnik: ReturnType<typeof setTimeout> | undefined;
    const ro = new ResizeObserver(([vnos]) => {
      if (casovnik) clearTimeout(casovnik);
      casovnik = setTimeout(() => {
        const { width, height } = vnos.contentRect;
        setVelikost({ sirina: Math.floor(width), visina: Math.floor(height) });
      }, 100);
    });
    ro.observe(el);
    return () => { if (casovnik) clearTimeout(casovnik); ro.disconnect(); };
  }, [vsebina, pdfNapaka]);

  const obNalaganju = useCallback(async (pdf: PDFDocumentProxy) => {
    setSteviloStrani(pdf.numPages);
    const prva = await pdf.getPage(1);
    const vp = prva.getViewport({ scale: 1 });
    setRazmerje(vp.height / vp.width);
  }, []);

  if (napaka) return <div className="flex items-center justify-center h-64 text-gray-400">{napaka}</div>;
  if (!vsebina) return <Nalaganje />;

  if (vsebina.tip === 'slika') {
    return <img src={vsebina.blobUrl} alt="priloga" className="w-full h-full object-contain bg-gray-800" />;
  }
  if (vsebina.tip === 'drugo' || pdfNapaka) {
    return <iframe src={vsebina.blobUrl} className="w-full h-full border-0" title="PDF" />;
  }

  // Širina strani — react-pdf "width" je širina prikazane (že zarotirane) strani.
  const r = rotacija % 180 === 0 ? razmerje : 1 / razmerje;
  const prostaSirina = Math.max(velikost.sirina - 2 * ODMIK - DRSNIK, 100);
  const prostaVisina = Math.max(velikost.visina - 2 * ODMIK, 100);
  const osnovnaSirina = nacin === 'sirina' ? prostaSirina : Math.min(prostaSirina, prostaVisina / r);
  const sirinaStrani = Math.round(osnovnaSirina * zoom);

  const povecaj = () => setZoom((z) => Math.min(MAX_ZOOM, +(z + KORAK_ZOOM).toFixed(2)));
  const pomanjsaj = () => setZoom((z) => Math.max(MIN_ZOOM, +(z - KORAK_ZOOM).toFixed(2)));
  const prilagodi = (n: 'sirina' | 'stran') => { setNacin(n); setZoom(1); };

  return (
    <div className="flex h-full min-h-0 flex-col bg-gray-800 text-gray-300">
      {/* Orodna vrstica */}
      <div className="flex shrink-0 items-center gap-0.5 border-b border-gray-700 px-2 py-1 text-xs">
        <span className="px-2 tabular-nums text-gray-400">{steviloStrani ? `${steviloStrani} str.` : ''}</span>
        <span className="mx-1 h-4 w-px bg-gray-600" />
        <Gumb naslov="Pomanjšaj" onClick={pomanjsaj}><ZoomOut size={15} /></Gumb>
        <span className="w-10 text-center tabular-nums">{Math.round(zoom * 100)}%</span>
        <Gumb naslov="Povečaj" onClick={povecaj}><ZoomIn size={15} /></Gumb>
        <span className="mx-1 h-4 w-px bg-gray-600" />
        <Gumb naslov="Prilagodi širini" aktiven={nacin === 'sirina' && zoom === 1} onClick={() => prilagodi('sirina')}><MoveHorizontal size={15} /></Gumb>
        <Gumb naslov="Cela stran" aktiven={nacin === 'stran' && zoom === 1} onClick={() => prilagodi('stran')}><Maximize size={15} /></Gumb>
        <Gumb naslov="Zavrti" onClick={() => setRotacija((x) => (x + 90) % 360)}><RotateCw size={15} /></Gumb>
        <div className="flex-1" />
        <a href={vsebina.blobUrl} download={imeDatoteke} title="Prenesi"
          className="h-7 w-7 flex items-center justify-center rounded hover:bg-gray-700"><Download size={15} /></a>
      </div>

      {/* Območje strani: zunanji element meri velikost, notranji drsi */}
      <div ref={okvirRef} className="relative min-h-0 flex-1">
        <div className="absolute inset-0 overflow-auto" style={{ scrollbarGutter: 'stable', padding: ODMIK }}>
          {velikost.sirina > 0 && datoteka && (
            <Document
              file={datoteka}
              onLoadSuccess={obNalaganju}
              onLoadError={() => setPdfNapaka(true)}
              loading={<Nalaganje />}
              error={null}
            >
              {Array.from({ length: steviloStrani }, (_, i) => (
                <div key={i} className="mx-auto mb-3 w-fit bg-white shadow-lg">
                  <Page
                    pageNumber={i + 1}
                    width={sirinaStrani}
                    rotate={rotacija}
                    renderTextLayer
                    renderAnnotationLayer={false}
                    loading={null}
                  />
                </div>
              ))}
            </Document>
          )}
        </div>
      </div>
    </div>
  );
}

// ───────────────────────── obstoječi vmesnik (propsi nespremenjeni) ─────────────────────────

export function PdfViewer({ companyId, docId }: { companyId: string; docId: string }) {
  const [pdfUrl, setPdfUrl] = useState<string|null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string|null>(null);

  useEffect(() => {
    if (!docId || !companyId) return;
    setLoading(true);
    setError(null);
    setPdfUrl(null);

    // Pridobi kratkolivni token in prikaži PDF
    api.get(`/dokumenti/${companyId}/${docId}/pdf-token`)
      .then(r => {
        const url = `${API_BASE}/dokumenti/${companyId}/${docId}/pdf-public?token=${r.data.token}`;
        setPdfUrl(url);
        setLoading(false);
      })
      .catch(() => { setError('PDF ni na voljo'); setLoading(false); });
  }, [docId, companyId]);

  if(loading) return <Nalaganje />;
  if(error) return <div className="flex items-center justify-center h-64 text-gray-400">{error}</div>;
  if(!pdfUrl) return null;
  return <PdfPlatno url={pdfUrl} />;
}

export function PdfWindow() {
  const [info, setInfo] = useState<{companyId:string, docId:string, token:string}|null>(null);
  const [pdfUrl, setPdfUrl] = useState<string|null>(null);

  useEffect(() => {
    const stored = localStorage.getItem('aicount_pdf_doc');
    if(stored) { try { setInfo(JSON.parse(stored)); } catch {} }

    const handler = (e: StorageEvent) => {
      if(e.key === 'aicount_pdf_doc') {
        if(e.newValue) { try { setInfo(JSON.parse(e.newValue)); } catch {} }
        else { setInfo(null); setPdfUrl(null); }
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  useEffect(() => {
    if(!info) return;
    setPdfUrl(`${API_BASE}/dokumenti/${info.companyId}/${info.docId}/pdf-public?token=${info.token}`);
  }, [info?.docId, info?.token]);

  if(!info || !pdfUrl) return (
    <div className="flex items-center justify-center h-screen bg-gray-900 text-gray-400 flex-col gap-4">
      <div className="text-5xl">📄</div>
      <p className="text-sm">Izberite račun v glavnem oknu</p>
    </div>
  );

  return (
    <div className="h-screen bg-gray-900 flex flex-col">
      <div className="px-4 py-2 bg-gray-800 text-xs text-gray-400">PDF Predogled</div>
      <div className="flex-1 min-h-0"><PdfPlatno key={pdfUrl} url={pdfUrl} /></div>
    </div>
  );
}
